import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Citation } from "@/api/types";
import { cn } from "@/lib/utils";

export interface CitationBlockProps {
  citations: Citation[];
  /**
   * Click handler for the timestamp pill. Receives the citation's
   * `start_ms`. Omit to render the timestamp as non-interactive text.
   */
  onJump?: (startMs: number) => void;
}

const INITIAL_VISIBLE = 2;
const QUOTE_TRUNCATE_LEN = 150;

/**
 * Format milliseconds → `MM:SS` (or `HH:MM:SS` past the hour). Mirrors
 * `TranscriptTab.formatTimestamp` but kept local to avoid a cross-page
 * import cycle (CitationBlock lives under components/, TranscriptTab under
 * pages/).
 */
function formatTimestamp(ms: number): string {
  const safe = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
}

/**
 * Inline question-mark icon used as the «verified=false» indicator. We
 * inline an SVG instead of pulling lucide-react (not in the bundle) — keeps
 * the dependency surface stable for v1.1.4.
 */
function HelpCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/**
 * Renders the per-artifact citation strip («[MM:SS] «цитата»…»). Used by
 * `ArtifactView` underneath every artifact whose backend payload carries a
 * non-empty `citations[]`. When the parent omits `onJump`, timestamps are
 * still shown but as plain text — clicking them does nothing.
 */
export function CitationBlock({ citations, onJump }: CitationBlockProps) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  if (citations.length === 0) return null;

  const visibleCount = showAll ? citations.length : INITIAL_VISIBLE;
  const visible = citations.slice(0, visibleCount);
  const hiddenCount = citations.length - INITIAL_VISIBLE;

  return (
    <div
      className="mt-2 space-y-1.5 border-l-2 border-muted pl-3 text-xs"
      data-testid="citation-block"
    >
      {visible.map((c, i) => (
        <CitationRow key={i} citation={c} onJump={onJump} />
      ))}
      {hiddenCount > 0 && !showAll && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={() => setShowAll(true)}
          data-testid="citation-show-more"
        >
          {t("meetings.card.citations.more_count", { n: hiddenCount })}
        </button>
      )}
      {showAll && citations.length > INITIAL_VISIBLE && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={() => setShowAll(false)}
          data-testid="citation-show-less"
        >
          {t("meetings.card.citations.less")}
        </button>
      )}
    </div>
  );
}

interface CitationRowProps {
  citation: Citation;
  onJump?: (startMs: number) => void;
}

function CitationRow({ citation, onJump }: CitationRowProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const hasTimestamp =
    typeof citation.start_ms === "number" && citation.start_ms >= 0;
  const isClickable = hasTimestamp && onJump !== undefined;
  const verifiedOff = citation.verified === false;
  const isAggregated = citation.evidence_type === "aggregated";

  const fullQuote = citation.quote ?? "";
  const isTruncatable = fullQuote.length > QUOTE_TRUNCATE_LEN;
  const visibleQuote =
    !isTruncatable || expanded
      ? fullQuote
      : `${fullQuote.slice(0, QUOTE_TRUNCATE_LEN).trimEnd()}…`;

  const timestamp = hasTimestamp
    ? formatTimestamp(citation.start_ms as number)
    : t("meetings.card.citations.noTimecodeLabel");

  const handleJump = () => {
    if (isClickable) {
      onJump(citation.start_ms as number);
    }
  };

  return (
    <div className="flex items-start gap-1.5" data-testid="citation-row">
      <TimestampPill
        timestamp={timestamp}
        onClick={isClickable ? handleJump : undefined}
      />

      <span className="flex-1 leading-relaxed text-muted-foreground">
        {isAggregated && (
          <span className="font-medium text-foreground/70">
            {t("meetings.card.citations.aggregated_prefix", {
              count: 1,
            })}
          </span>
        )}
        <span className="italic" data-testid="citation-quote">
          «{visibleQuote}»
        </span>
        {isTruncatable && (
          <>
            {" "}
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              onClick={() => setExpanded((v) => !v)}
              data-testid="citation-expand-toggle"
            >
              {expanded
                ? t("meetings.card.citations.collapse")
                : t("meetings.card.citations.expand")}
            </button>
          </>
        )}
        {verifiedOff && (
          <span
            className="ml-1 inline-flex items-center align-middle text-muted-foreground"
            title={t("meetings.card.citations.verified_off")}
            aria-label={t("meetings.card.citations.verified_off")}
            data-testid="citation-unverified"
          >
            <HelpCircleIcon className="inline-block" />
          </span>
        )}
      </span>
    </div>
  );
}

interface TimestampPillProps {
  timestamp: string;
  onClick?: () => void;
}

function TimestampPill({ timestamp, onClick }: TimestampPillProps) {
  const { t } = useTranslation();
  const label = `[${timestamp}]`;
  if (onClick === undefined) {
    return (
      <span
        className="shrink-0 font-mono tabular-nums text-muted-foreground"
        data-testid="citation-timestamp-static"
      >
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 rounded font-mono tabular-nums text-blue-600",
        "hover:underline focus-visible:outline-none focus-visible:ring-1",
        "focus-visible:ring-ring",
      )}
      data-testid="citation-timestamp"
      aria-label={t("meetings.card.citations.jumpAria", { time: timestamp })}
    >
      {label}
    </button>
  );
}

/**
 * Small grey badge used to mark artifacts that came back with no
 * citations at all (LLM didn't supply evidence). Rendered by
 * `ArtifactView` in the artifact's corner, not by `CitationBlock` itself.
 */
export function NoCitationsBadge() {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
      data-testid="no-citations-badge"
    >
      {t("meetings.card.citations.no_citations_badge")}
    </span>
  );
}
